<script setup>
import { computed, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch } from 'vue';

// // Store management imports
import { useAwsStore } from '@/stores/aws';
import { useBreakStore } from '@/stores/break';
import { useTrackerStore } from '@/stores/tracker';

// // Utils imports
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { logDebug, logError, logWarn } from '@/utils/logger';

// // Composables
import { usePollResults } from '@/composables/usePollResults';

// // Module imports
import PollInput from './PollInput.vue';
import PollOutput from './PollOutput.vue';
import { PollSimpleOptions } from './pollSimpleUtils';

// // Asset imports

const appInstance = import.meta.env.VITE_APP_ENV;

const storeAws = useAwsStore();
const storeBreak = useBreakStore();
const storeTracker = useTrackerStore();
const awsCredentialProvider = ref(null); // To store the AWS credential provider (reactive)

const isKeyCurrentlyPressed = ref(false);
const hasOptionBeenSelected = ref(false);
const hasNavigationFrozen = ref(false);
const isTemplateReadyToFadeIn = ref(false);
const activeIndex = ref(0);
const pressedKey = ref('');

const { calculatedPollPercentages, getSelectionHandler, castVote } = usePollResults({
  optionKeys: Object.values(PollSimpleOptions),
  getBreakUuid: () => storeBreak.breakUuid,
  getAwsCredentialProvider: () => awsCredentialProvider.value,
});

const styleObjectMain = reactive({
  background: 'linear-gradient(107deg, #BF4AE9 15.55%, #A323A6 64.08%)',
});

const getPollColors = computed(() => {
  return {
    bar: 'rgba(255, 255, 255, 1)',
    resultsBox: 'rgba(255, 255, 255, 1)',
  };
});

// // EmojiPoll handler functions

const keyDownHandler = (e) => {
  CommunicationUtil.onKeyPressed(e.keyCode);
  isKeyCurrentlyPressed.value = true;
  pressedKey.value = e.key;
};

const keyUpHandler = () => {
  isKeyCurrentlyPressed.value = false;
  pressedKey.value = '';
};

const buttonSelectHandler = async () => {
  // castVote handles the get-then-put fallback, the optimistic result, and
  // scheduling the graceful update + bounded retry — see usePollResults.
  await castVote(activeIndex.value, storeBreak.breakOptions[activeIndex.value].optionLabel);

  hasOptionBeenSelected.value = true;

  // Update beacon with accept invitation event
  if (storeTracker.isIframe && ['dev', 'stage', 'prod'].includes(appInstance)) {
    if (storeTracker.trackingInfo) {
      if (
        storeTracker.trackingInfo.trackingEvents &&
        storeTracker.trackingInfo.trackingEvents.length > 0
      ) {
        storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
          storeTracker.trackingInfo,
          'acceptInvitation',
          storeBreak.breakUuid,
        );
      }
    }
  }
};

// const templateFadeInHandler = (templateFadeInStateParam) => {
//   setTimeout(() => {
//     if (templateFadeInStateParam) {
//       isTemplateReadyToFadeIn.value = true;
//     }
//   }, 500);
// };
const templateFadeInHandler = () => {
  setTimeout(() => {
    isTemplateReadyToFadeIn.value = true;
  }, 500);
};

// // EmojiPoll lifecycle hooks

onBeforeMount(async () => {
  try {
    // Initialize AWS Cognito credentials and backendApis first
    const identityPoolId = import.meta.env.VITE_APP_IDENTITY_POOL_ID;

    if (identityPoolId) {
      if (storeAws.isInitialized.value && storeAws.awsCredentialProvider.value) {
        // Use existing AWS credentials from store
        awsCredentialProvider.value = storeAws.awsCredentialProvider.value;
      } else {
        // Initialize AWS credentials and signer
        awsCredentialProvider.value = await storeAws.initializeAwsCredentials(
          identityPoolId,
          import.meta.env.VITE_APP_AWS_REGION || 'us-east-1',
        );
        logDebug('AWS Cognito initialized successfully in TempEmojiPoll (onBeforeMount).');
      }
    } else {
      logWarn('VITE_APP_IDENTITY_POOL_ID is not set. AWS calls may not be authenticated.');
    }

    // Note: API calls will be made in the isBreakReady watcher to avoid race condition
  } catch (error) {
    logError('Error during onBeforeMount in TempEmojiPoll:', error);
  }
});

onMounted(() => {
  document.addEventListener('keydown', keyDownHandler);
  document.addEventListener('keyup', keyUpHandler);
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
  document.removeEventListener('keyup', keyUpHandler);
});

// // EmojiPoll watchers

watch(isKeyCurrentlyPressed, (isKeyCurrentlyPressedNewVal) => {
  if (isKeyCurrentlyPressedNewVal) {
    // logInfo('EmojiPoll – Key is pressed down');
    // logInfo(`pressedKey.value: ${pressedKey.value}`);

    if (!hasOptionBeenSelected.value && !hasNavigationFrozen.value) {
      if (pressedKey.value === 'ArrowDown') {
        // if (activeIndex.value < props.pollOptions.length - 1) {
        if (activeIndex.value < storeBreak.breakOptions.length - 1) {
          activeIndex.value++;
        }
      } else if (pressedKey.value === 'ArrowUp') {
        if (activeIndex.value > 0) {
          activeIndex.value--;
        }
      } else if (pressedKey.value === 'Enter') {
        hasNavigationFrozen.value = true;

        // logDebug(`Button Selected – optionLabel: ${props.pollOptions[activeIndex.value].optionLabel}`);
        logDebug(
          `Button Selected – optionLabel: ${storeBreak.breakOptions[activeIndex.value].optionLabel}`,
        );
        // emit('buttonSelected', e);

        buttonSelectHandler();
      }
    }
  }
});

// Watch for isBreakReady to become true before making API calls
// This prevents the race condition where breakUuid is not yet available
watch(
  [() => storeBreak.isBreakReady, awsCredentialProvider],
  ([isBreakReadyNewVal, credentialProvider]) => {
    if (isBreakReadyNewVal && credentialProvider) {
      logDebug('isBreakReady is true and credentials are ready, fetching poll data');
      // No vote yet; do a background call to get raw starting results
      getSelectionHandler('isBreakReadyWatcher');
    }
  },
);
</script>

<template>
  <section
    class="emoji-poll"
    :class="{
      'is-clear': !isTemplateReadyToFadeIn,
      'is-opaque': isTemplateReadyToFadeIn,
    }"
  >
    <div class="emoji-poll__container">
      <div
        class="emoji-poll__scrim"
        :class="{
          'emoji-poll__scrim--upper-left': storeBreak.breakScreenPlacement === 'upper_left',
          'emoji-poll__scrim--upper-right': storeBreak.breakScreenPlacement === 'upper_right',
          'emoji-poll__scrim--lower-left': storeBreak.breakScreenPlacement === 'lower_left',
          'emoji-poll__scrim--lower-right': storeBreak.breakScreenPlacement === 'lower_right',
        }"
      ></div>

      <PollInput
        v-if="!hasOptionBeenSelected"
        :input-box-background="styleObjectMain"
        :ad-tagline="storeBreak.breakTaglineCopy"
        :ad-tagline-color="storeBreak.breakTaglineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :poll-placement="storeBreak.breakScreenPlacement"
        :brand-logos="storeBreak.breakSponsorLogos"
        :break-uuid="storeBreak.breakUuid"
        @poll-readiness-emitted="templateFadeInHandler"
      />
      <PollOutput
        v-else-if="hasOptionBeenSelected"
        :emoji-only="true"
        :output-box-background="styleObjectMain"
        :ad-tagline="storeBreak.breakTaglineCopy"
        :ad-tagline-color="storeBreak.breakTaglineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :poll-placement="storeBreak.breakScreenPlacement"
        :brand-logos="storeBreak.breakSponsorLogos"
        :break-uuid="storeBreak.breakUuid"
        :poll-percentages="calculatedPollPercentages"
        :bar-color="getPollColors.bar"
        :results-box-background-color="getPollColors.resultsBox"
      />
    </div>
  </section>
</template>
